import { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { shipProductionOrder } from "../api/productionOrders";

const MAX_IMAGES = 3;

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const commaIndex = result.indexOf(",");
      resolve({
        file_name: file.name,
        mime_type: file.type || "image/jpeg",
        data_base64: commaIndex >= 0 ? result.slice(commaIndex + 1) : result,
        preview_url: result,
      });
    };
    reader.onerror = () => reject(new Error("Dosya okunamadı"));
    reader.readAsDataURL(file);
  });
}

export default function WorkOrderShipmentDialog({ workOrder, onClose, onSuccess }) {
  const [koli, setKoli] = useState("");
  const [images, setImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  async function onFilesSelected(event) {
    const files = Array.from(event?.target?.files || []);
    if (!files.length) return;
    try {
      const remaining = Math.max(0, MAX_IMAGES - images.length);
      if (remaining <= 0) {
        setError(`En fazla ${MAX_IMAGES} fotoğraf ekleyebilirsiniz.`);
        return;
      }
      const parsed = await Promise.all(files.slice(0, remaining).map(readFileAsBase64));
      setImages((prev) => [...prev, ...parsed]);
      setError("");
    } catch (err) {
      setError(err?.message || "Fotoğraflar seçilemedi");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  function removeImage(idx) {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit() {
    setError("");
    const qty = Number(koli);
    if (!Number.isInteger(qty) || qty <= 0) {
      setError("Sevk koli adedi pozitif bir tam sayı olmalı.");
      return;
    }

    setSaving(true);
    try {
      const result = await shipProductionOrder(
        Number(workOrder.id),
        qty,
        images.map((img) => ({
          file_name: img.file_name,
          mime_type: img.mime_type,
          data_base64: img.data_base64,
        }))
      );
      await onSuccess?.(result);
      onClose?.();
    } catch (err) {
      console.error("WORK ORDER SHIPMENT ERROR", err);
      const status = err?.response?.status;
      const data = err?.response?.data;
      const message =
        data?.error?.message ||
        data?.message ||
        (typeof data === "string" ? data : null) ||
        err?.message ||
        null;
      setError(`Sevk yapılamadı${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", boxShadow: "0 24px 70px rgba(15, 23, 42, 0.22)" } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.5, color: "#fff", background: "linear-gradient(135deg, #312e81 0%, #4f46e5 56%, #6366f1 100%)" }}>
          <Stack direction="row" alignItems="center" spacing={1.5}><Box sx={{ width: 42, height: 42, borderRadius: 2.25, display: "grid", placeItems: "center", bgcolor: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.24)", fontSize: 19 }}>↗</Box><Box><Typography variant="h6" fontWeight={800}>İş Emri Sevk</Typography><Typography variant="body2" sx={{ opacity: 0.8 }}>Mamul stoktan sevk edilecek koli adedini girin.</Typography></Box></Stack>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f7f8fc" }}>
        {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert> : null}
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, border: "1px solid #e7eaf3", bgcolor: "#fff" }}>
          <Box sx={{ mb: 2 }}><Typography fontWeight={800}>Sevk bilgisi</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {workOrder?.order_no || "-"} iş emrinin mamul stokundan sevk yapılacak.
            Mamul stokta koli olarak durduğu için girilen adet doğrudan stoktan düşülür.
          </Typography></Box>
          <TextField
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.25 } }}
            autoFocus
            label="Sevk Koli Adedi"
            type="number"
            value={koli}
            onChange={(e) => setKoli(e.target.value)}
            inputProps={{ min: 1, step: 1 }}
            disabled={saving}
            required
            fullWidth
          />

          <Box sx={{ mt: 2.5, pt: 2.5, borderTop: "1px dashed #e7eaf3" }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: images.length ? 1.5 : 0 }}>
              <Button
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
                disabled={saving || images.length >= MAX_IMAGES}
                sx={{ borderRadius: 2 }}
              >
                Fotoğraf Ekle
              </Button>
              <Typography variant="body2" color="text.secondary">
                En fazla {MAX_IMAGES} fotoğraf
              </Typography>
            </Stack>

            <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={onFilesSelected} />

            {images.length ? (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {images.map((img, idx) => (
                  <Stack key={`${img.file_name || "img"}-${idx}`} spacing={0.5} alignItems="flex-start">
                    <Box
                      component="img"
                      src={img.preview_url}
                      alt={img.file_name || `sevk-${idx + 1}`}
                      sx={{ width: 88, height: 88, objectFit: "cover", borderRadius: 1.5, border: "1px solid", borderColor: "divider" }}
                    />
                    <Button size="small" color="error" onClick={() => removeImage(idx)} disabled={saving}>
                      Kaldır
                    </Button>
                  </Stack>
                ))}
              </Stack>
            ) : null}
          </Box>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3.5 }, py: 2, bgcolor: "#fff", borderTop: "1px solid #e7eaf3", justifyContent: "space-between" }}>
        <Typography variant="caption" color="text.secondary">* Onay sonrası sevk miktarı stoktan düşülür.</Typography>
        <Stack direction="row" spacing={1}><Button onClick={onClose} color="inherit" disabled={saving}>İptal</Button><Button onClick={handleSubmit} variant="contained" disabled={saving} sx={{ px: 2.5, borderRadius: 2, boxShadow: "0 8px 18px rgba(79, 70, 229, 0.24)" }}>{saving ? "Onaylanıyor..." : "Sevki Onayla"}</Button></Stack>
      </DialogActions>
    </Dialog>
  );
}
